/* eslint-disable no-underscore-dangle */
import React, { useRef, useCallback, useEffect, memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { VariableSizeList as List, areEqual } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import ContentBlock from './ContentBlock';
import ExtractSkeletonRow from './ExtractSkeletonRow';
import useScrollToBlock from './useScrollToBlock';
import ButtonAddBlock from '../../component/ButtonAddBlock';

const DEFAULT_ROW_HEIGHT = 160;
const SKELETON_ROW_HEIGHT = 140;
const SKELETON_ROW_KEYS = [
  'skeleton-1',
  'skeleton-2',
  'skeleton-3',
  'skeleton-4',
];

const VirtualizedRow = memo(({ index, style, data }) => {
  const {
    saveProject,
    onOpenPopupNotEnoughCredits,
    blockIds,
    blocks,
    originalBlocks,
    isFiltering,
    renderContentBlockProps,
    setRowHeight,
    showAddButton,
    addButtonProps,
    setDeleteDialogData,
    blockBlackWordsMap,
    allBlockIds,
    allBlocks,
  } = data;
  const isAddButton = showAddButton && index === blockIds.length;
  const blockId = isAddButton ? null : blockIds[index];
  // Each row reads its own highlight (a boolean), so a block change re-renders
  // only the two rows whose highlight flips — not every row through itemData.
  const isHighlight = useSelector(
    (state) => blockId != null && state.project.currentBlockId === blockId,
  );
  // Full, unfiltered ordered id list — `multiBlock.blockIds`. Note this is a
  // different collection from `multiBlock.originalBlockIds`, despite the local
  // name below. Previously read here via its own useSelector, which subscribed
  // every row to the whole `multiBlock` container; that container's identity
  // changes on any single-block edit, so one edit re-rendered every mounted row
  // regardless of memo. Now handed down through itemData instead, keeping the
  // original local name so the gap / consecutive logic below is untouched.
  const originalBlockIds = allBlockIds;
  const nextBlockId =
    blockId === blockIds[blockIds.length - 1] ? null : blockIds[index + 1];
  const filteredNextBlock = nextBlockId ? blocks[nextBlockId] : null;
  const prevBlockId = index === 0 ? null : blockIds[index - 1];
  const prevBlock = prevBlockId ? blocks[prevBlockId] : null;

  // When filtering or when blockIds length differs from originalBlockIds,
  // find the chronologically next block for gap calculation
  // Otherwise use the filtered next block (which is the same as chronological when not filtering)
  const isFilteredList = blockIds.length !== originalBlockIds?.length;
  const chronoNextBlockId = useMemo(() => {
    if (!blockId || !isFilteredList || !originalBlockIds) return null;
    const currentIndex = originalBlockIds.indexOf(blockId);
    return currentIndex >= 0 && currentIndex < originalBlockIds.length - 1
      ? originalBlockIds[currentIndex + 1]
      : null;
  }, [blockId, isFilteredList, originalBlockIds]);
  const chronoNextBlock =
    chronoNextBlockId && allBlocks ? allBlocks[chronoNextBlockId] : null;

  // Use chronologically next block when list is filtered, otherwise use filtered next block
  const nextBlock =
    isFilteredList && chronoNextBlock ? chronoNextBlock : filteredNextBlock;

  // Check if current block and next block are consecutive in original order
  // Use filteredNextBlock (not chronoNextBlock) for this check
  const areBlocksConsecutive = useMemo(() => {
    if (!isFiltering || !filteredNextBlock) return true;

    const currentIndex = originalBlockIds.indexOf(blockId);
    const nextIndex = originalBlockIds.indexOf(filteredNextBlock.id);

    // If either block is not found in originalBlockIds, consider them not consecutive
    if (currentIndex === -1 || nextIndex === -1) return false;

    // Check if next block is immediately after current block
    return nextIndex === currentIndex + 1;
  }, [isFiltering, blockId, filteredNextBlock, originalBlockIds]);
  const rowRef = useRef(null);

  useEffect(() => {
    if (!rowRef.current) return undefined;
    const resizeObserver = new ResizeObserver(([entry]) => {
      // `blockId` as well as `index`: the measurement is cached per block (a row
      // index describes a different block after an insert/delete), while
      // react-window still invalidates its own metadata by index.
      if (entry) setRowHeight(index, blockId, entry.contentRect.height);
    });
    resizeObserver.observe(rowRef.current);
    return () => resizeObserver.disconnect();
  }, [index, blockId, setRowHeight]);

  if (isAddButton) {
    return (
      <div style={style}>
        <ButtonAddBlock {...addButtonProps} />
      </div>
    );
  }

  return (
    <div style={style}>
      <div
        ref={rowRef}
        style={{ padding: '24px 24px 16px 0', boxSizing: 'border-box' }}
        key={blockId}
      >
        <ContentBlock
          {...{
            saveProject,
            onOpenPopupNotEnoughCredits,
            audioType: renderContentBlockProps.audioType,
            blockId,
            block: blocks[blockId],
            originalBlock: originalBlocks[blockId],
            disabledMoveUp: index === 0,
            characterExceed: renderContentBlockProps.characterExceed,
            isLastBlock: blockId === blockIds[blockIds.length - 1],
            onChangeCharacterExceed:
              renderContentBlockProps.onChangeCharacterExceed,
            onAddNewParagraphs: renderContentBlockProps.onAddNewParagraphs,
            onSelectParagraphId: renderContentBlockProps.onSelectParagraphId,
            onOpenDiscardedVoiceCloningDialog:
              renderContentBlockProps.onOpenDiscardedVoiceCloningDialog,
            isFocusing: renderContentBlockProps.focusingBlockId === blockId,
            onFocusingBlock: renderContentBlockProps.onFocusingBlock,
            onDragEnd: renderContentBlockProps.onDragEnd,
            onSaveProject: renderContentBlockProps.onSaveProject,
            voiceDefault: renderContentBlockProps.voiceDefault,
            isDownloading: renderContentBlockProps.isDownloading,
            onOpenOutOfCreditsDialog:
              renderContentBlockProps.onOpenOutOfCreditsDialog,
            checkValidCreateSynthesis:
              renderContentBlockProps.checkValidCreateSynthesis,
            isHighlight,
            handlePlayAudio: renderContentBlockProps.handlePlayAudio,
            onOpenWrongSyntaxDialog:
              renderContentBlockProps.onOpenWrongSyntaxDialog,
            onOpenWrongSyntaxDialogV2:
              renderContentBlockProps.onOpenWrongSyntaxDialogV2,
            projectId: renderContentBlockProps.projectId,
            isPlayingBlock:
              renderContentBlockProps.isPlayingBlock &&
              renderContentBlockProps.currentPlayingBlockId === blockId,
            onConvertBlockToAudio:
              renderContentBlockProps.onConvertBlockToAudio,
            enableDubbingBlockAudio:
              renderContentBlockProps.enableDubbingBlockAudio,
            canDownloadBlockAudio:
              renderContentBlockProps.canDownloadBlockAudio,
            currentPlayingBlockId:
              renderContentBlockProps.currentPlayingBlockId,
            onSeekInBlock: renderContentBlockProps.onSeekInBlock,
            blockAudioCurrentTime:
              renderContentBlockProps.blockAudioCurrentTime,
            blockAudioDuration: renderContentBlockProps.blockAudioDuration,
            isPlaying: renderContentBlockProps.isPlaying,
            hasVideoTimeline: renderContentBlockProps.hasVideoTimeline,
            setDeleteDialogData,
            onMergeBlock: renderContentBlockProps.onMergeBlock,
            speakers: renderContentBlockProps.speakers,
            nextBlock,
            prevBlock,
            areBlocksConsecutive,
            foundBlackWords: blockBlackWordsMap?.[blockId] || [],
            isBlockUIImprovement: renderContentBlockProps.isBlockUIImprovement,
            enableCreditUsagePreview:
              renderContentBlockProps.enableCreditUsagePreview,
            isBlockSelectionLocked:
              renderContentBlockProps.isBlockSelectionLocked,
          }}
        />
      </div>
    </div>
  );
  // `areEqual` compares `style` by value (react-window hands rows a fresh style
  // object whenever the size cache is reset) and the remaining props by
  // reference. It only pays off because `itemData` is memoized below — a new
  // `data` identity each render would defeat any comparator.
}, areEqual);

const VirtualizedContentList = ({
  saveProject,
  onOpenPopupNotEnoughCredits,
  blockIds,
  blocks,
  originalBlocks,
  allBlockIds = [],
  allBlocks = {},
  isFiltering = false,
  renderContentBlockProps,
  containerHeight,
  showAddButton = false,
  addButtonProps = {},
  setDeleteDialogData,
  blockBlackWordsMap = {},
  isSkeletonUI = false,
}) => {
  const listRef = useRef(null);
  // Measured row heights, keyed by BLOCK ID rather than row index: an index
  // describes a different block after an insert, delete or reorder, so an
  // index-keyed cache silently hands react-window the previous occupant's height
  // and the offsets it derives from that are wrong for the rest of the list.
  const rowHeights = useRef({});
  const prevBlockIds = useRef(blockIds);
  // Read by `getRowHeight` through a ref so that callback keeps empty deps —
  // changing the identity of `itemSize` would make react-window rebuild the
  // metadata it has already cached.
  const blockIdsRef = useRef(blockIds);
  blockIdsRef.current = blockIds;

  // Show skeleton placeholders only during the initial phase before any blocks arrive.
  const showSkeletonOnly = isSkeletonUI && blockIds.length === 0;

  const setRowHeight = useCallback((index, blockId, size) => {
    const newSize = Math.max(size, 80);
    if (rowHeights.current[blockId] !== newSize) {
      rowHeights.current[blockId] = newSize;
      listRef.current?.resetAfterIndex(index, true);
    }
  }, []);

  const getRowHeight = useCallback(
    (index) =>
      rowHeights.current[blockIdsRef.current[index]] || DEFAULT_ROW_HEIGHT,
    [],
  );

  const scrollToBottom = useCallback(() => {
    if (listRef.current) {
      // Access the outer element
      // eslint-disable-next-line no-underscore-dangle
      const outerElement = listRef.current._outerRef;

      if (outerElement) {
        outerElement.scrollTop = outerElement.scrollHeight;
      }
    }
  }, [listRef]);

  const findInsertionIndex = (current, previous) => {
    for (let i = 0; i < current.length; i += 1) {
      if (current[i] !== previous[i]) return i;
    }
    return -1;
  };

  useEffect(() => {
    const current = blockIds.join(',');
    const previous = prevBlockIds.current.join(',');

    if (current === previous || !listRef.current) return;

    const prevLength = prevBlockIds.current.length;
    const currentLength = blockIds.length;

    const isAddingAtEnd =
      currentLength === prevLength + 1 &&
      prevBlockIds.current.every((id, idx) => id === blockIds[idx]);

    const isRemovingAtEnd =
      currentLength === prevLength - 1 &&
      blockIds.every((id, idx) => id === prevBlockIds.current[idx]);

    const insertionIndex =
      currentLength === prevLength + 1 && !isAddingAtEnd
        ? findInsertionIndex(blockIds, prevBlockIds.current)
        : -1;

    if (isAddingAtEnd) {
      listRef.current.resetAfterIndex(prevLength, false);
      setTimeout(scrollToBottom, 100);
    } else if (insertionIndex !== -1) {
      listRef.current.resetAfterIndex(insertionIndex, false);
    } else if (isRemovingAtEnd) {
      listRef.current.resetAfterIndex(currentLength, false);
    } else {
      listRef.current.resetAfterIndex(0, true);
    }

    prevBlockIds.current = [...blockIds];
  }, [blockIds.length, blockIds.join(','), scrollToBottom]);

  useEffect(() => {
    if (renderContentBlockProps.focusingBlockId && listRef.current) {
      const blockIndex = blockIds.indexOf(
        renderContentBlockProps.focusingBlockId,
      );
      if (blockIndex !== -1) {
        setTimeout(
          () => listRef.current.resetAfterIndex(blockIndex, true),
          100,
        );
      }
    }
  }, [renderContentBlockProps.focusingBlockId, blockIds]);

  // Scroll to a requested block (block with an error, a freshly added block, a
  // clip clicked on the voiceover track).
  useScrollToBlock({
    listRef,
    blockIds,
    rowHeights,
    scrollToBlockId: renderContentBlockProps.scrollToBlockId,
    align: renderContentBlockProps.scrollToBlockAlign,
  });

  // react-window forwards `itemData` to every row as its `data` prop, so a fresh
  // object literal here would break `memo`/`areEqual` on all rows every render.
  // Must stay above the early return below — hooks run unconditionally.
  const itemData = useMemo(
    () => ({
      saveProject,
      onOpenPopupNotEnoughCredits,
      blockIds,
      setRowHeight,
      blocks,
      originalBlocks,
      allBlockIds,
      allBlocks,
      isFiltering,
      renderContentBlockProps,
      showAddButton,
      addButtonProps,
      setDeleteDialogData,
      blockBlackWordsMap,
    }),
    [
      saveProject,
      onOpenPopupNotEnoughCredits,
      blockIds,
      setRowHeight,
      blocks,
      originalBlocks,
      allBlockIds,
      allBlocks,
      isFiltering,
      renderContentBlockProps,
      showAddButton,
      addButtonProps,
      setDeleteDialogData,
      blockBlackWordsMap,
    ],
  );

  const itemKey = useCallback((rowIndex) => blockIds[rowIndex], [blockIds]);

  // No blocks yet during streaming extraction — show skeleton placeholders.
  if (showSkeletonOnly) {
    return (
      <div
        style={{
          height: containerHeight || '70vh',
          width: '100%',
          overflow: 'auto',
          padding: '8px 0',
        }}
      >
        {SKELETON_ROW_KEYS.map((key) => (
          <ExtractSkeletonRow
            key={key}
            style={{ height: SKELETON_ROW_HEIGHT }}
          />
        ))}
      </div>
    );
  }

  const totalItemCount = blockIds.length + (showAddButton ? 1 : 0);

  return (
    <div
      style={{
        height: containerHeight || '70vh',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <AutoSizer>
        {({ height, width }) => (
          <List
            ref={listRef}
            height={height}
            width={width}
            className="custom-scrollbar"
            itemCount={totalItemCount}
            itemSize={getRowHeight}
            // Must match `getRowHeight`'s fallback. react-window defaults it to
            // 50, and uses it for the list's total height — which then clamps the
            // offset `scrollToItem` may use. With a 160 fallback and a 50
            // estimate the two disagree, and a jump to a far block was clamped to
            // roughly a third of its real offset, landing on the wrong block.
            estimatedItemSize={DEFAULT_ROW_HEIGHT}
            // Each row mounts a draft-js Editor, so overscan is expensive here:
            // 10 kept ~20 extra editors alive off-screen.
            overscanCount={3}
            itemData={itemData}
            itemKey={itemKey}
            threshold={200}
            style={{ scrollbarWidth: 'thin' }}
          >
            {VirtualizedRow}
          </List>
        )}
      </AutoSizer>
    </div>
  );
};

export default VirtualizedContentList;
